import React, { useState, useCallback } from 'react';
import StepWizard from 'react-step-wizard';
import styled from 'styled-components';

import ActivateVoice from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/ActivateVoice';
import ExistSearchPhoneNumbers from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/ExistSearchPhoneNumbers';
import { getAgentDesktopURL } from 'src/presentation/components/pages/shared/utils/Utils';
import LinkPhoneNumberToIVR from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/LinkPhoneNumberToIVR';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import NewSearchPhoneNumbers from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/NewSearchPhoneNumbers';
import { NumberPreference } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import { PhoneNumberCapability, PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import PhoneNumberPurchased from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/PhoneNumberPurchased';
import PortPhoneNumber from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/PortPhoneNumberVoice';
import ReviewPhoneNumber from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/ReviewPhoneNumber';
import SelectIvr from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SelectIvr';
import SelectPhonePreferenceForm from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SelectPhonePreference';
import SetEntryPoint from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SetEntryPoint';
import SetupComplete from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SetupComplete';
import {
  StageConfig,
  StageConfigState,
  getStagesList,
} from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  ModalContainer,
  StyledStepContainer,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import PhoneSmsVerification from './templates/PhoneSmsVerification';
import PhoneSmsVerificationPortOrForward from './templates/PhoneSmsVerificationPortOrForward';

type ConfigureNumberModalModalType = {
  handleCloseModal: () => void;
};

export const initialState: StageConfigState = {
  numberPreference: NumberPreference.new,
  activateVoice: true,
  linkIvr: true,
  ivr: {
    initialIvrId: '',
    queueIvrId: '',
    queueIvrWaitDuration: '',
  },
  selectedPhone: {
    id: '',
    countryCode: '',
    locality: '',
    region: '',
    phoneNumber: '',
    phoneNumberType: PhoneNumberType.LOCAL,
    capabilities: [],
  },
};

const ConfigureVoiceModal = ({ handleCloseModal }: ConfigureNumberModalModalType) => {
  const organizationSupportLink = getAgentDesktopURL('support.html');
  const [stageConfig, updateStageConfig] = useState(initialState);
  const [activeStep, saveWizardStep] = useState(1);

  const saveFormData = useCallback(
    newData => {
      updateStageConfig({ ...stageConfig, ...newData });
    },
    [stageConfig]
  );

  const setInitState = useCallback(() => {
    updateStageConfig(initialState);
  }, []);

  // Configuration of Stepper Component to manage different amount of steps
  // `stageId` - unique name for each stage
  // `stage` - current stage component
  // 1. nextStages = [] - we use it for last step
  // 2. nextStages = [StageConfig] - if next step always has the same template we can provide single object with stageId
  // 3. nextStages = [StageConfig[]] - if we have dynamic rules we can use list of StageConfig items.
  //    The main part of this logic is `showStage` field.
  //    If `showStage` equal true we show this template as a next one. Rest of templates from `nextStages` will be ignore.
  const { numberPreference } = stageConfig;

  const {
    selectedPhone: { capabilities },
  } = stageConfig;
  const phoneHasSmsOrMmsCapability =
    capabilities.includes(PhoneNumberCapability.SMS) || capabilities.includes(PhoneNumberCapability.MMS);

  const stagesSchema: StageConfig[] = [
    {
      stageId: 'SelectPhonePreferenceForm',
      stage: <SelectPhonePreferenceForm saveStep={saveFormData} stageConfig={stageConfig} />,
      nextStages: [
        { stageId: `NewSearchPhoneNumbers`, showStage: numberPreference === NumberPreference.new },
        { stageId: `ExistSearchPhoneNumbers`, showStage: numberPreference === NumberPreference.exists },
        { stageId: `PortPhoneNumber`, showStage: numberPreference === NumberPreference.port },
      ],
    },

    {
      stageId: 'NewSearchPhoneNumbers',
      stage: <NewSearchPhoneNumbers saveStep={saveFormData} organizationSupportLink={organizationSupportLink} />,
      nextStages: [{ stageId: 'ReviewPhoneNumber' }],
    },
    {
      stageId: 'ExistSearchPhoneNumbers',
      stage: <ExistSearchPhoneNumbers saveStep={saveFormData} />,
      nextStages: [
        { stageId: 'PhoneSmsVerification-ExistingPhone', showStage: phoneHasSmsOrMmsCapability },
        { stageId: 'LinkPhoneNumberToIVR', showStage: !phoneHasSmsOrMmsCapability },
      ],
    },
    {
      stageId: 'PhoneSmsVerification-ExistingPhone',
      stage: <PhoneSmsVerification stageConfig={stageConfig} />,
      nextStages: [{ stageId: 'LinkPhoneNumberToIVR' }],
    },
    {
      stageId: 'PortPhoneNumber',
      stage: <PortPhoneNumber setInitState={setInitState} />,
      nextStages: [{ stageId: 'PhoneSmsVerificationPortOrForward' }],
    },
    {
      stageId: 'PhoneSmsVerificationPortOrForward',
      stage: <PhoneSmsVerificationPortOrForward closeModal={handleCloseModal} />,
      nextStages: [],
    },
    {
      stageId: 'ReviewPhoneNumber',
      stage: <ReviewPhoneNumber phoneData={stageConfig.selectedPhone} />,
      nextStages: [{ stageId: 'PhoneNumberPurchased' }],
    },
    {
      stageId: 'PhoneNumberPurchased',
      stage: <PhoneNumberPurchased phoneNumber={stageConfig.selectedPhone.phoneNumber} />,
      nextStages: [
        { stageId: 'ActivateVoice', showStage: !phoneHasSmsOrMmsCapability },
        { stageId: 'PhoneSmsVerification-NewPhone', showStage: phoneHasSmsOrMmsCapability },
      ],
    },
    {
      stageId: 'PhoneSmsVerification-NewPhone',
      stage: <PhoneSmsVerification stageConfig={stageConfig} />,
      nextStages: [{ stageId: 'ActivateVoice' }],
    },
    {
      stageId: 'ActivateVoice',
      stage: (
        <ActivateVoice
          saveStep={saveFormData}
          phoneNumber={stageConfig.selectedPhone.phoneNumber}
          stageConfig={stageConfig}
        />
      ),
      nextStages: [
        { stageId: 'LinkPhoneNumberToIVR', showStage: stageConfig.activateVoice },
        { stageId: 'SetupComplete', showStage: !stageConfig.activateVoice },
      ],
    },
    {
      stageId: `LinkPhoneNumberToIVR`,
      stage: <LinkPhoneNumberToIVR phoneNumber={stageConfig.selectedPhone} saveStep={saveFormData} />,
      nextStages: [
        { stageId: 'SelectIvr', showStage: stageConfig.linkIvr },
        { stageId: 'SetEntryPoint', showStage: !stageConfig.linkIvr },
      ],
    },
    {
      stageId: `SelectIvr`,
      stage: <SelectIvr phoneData={stageConfig.selectedPhone} saveStep={saveFormData} />,
      nextStages: [{ stageId: 'SetEntryPoint' }],
    },
    {
      stageId: `SetEntryPoint`,
      stage: <SetEntryPoint phoneNumber={stageConfig.selectedPhone.phoneNumber} />,
      nextStages: [{ stageId: 'SetupComplete' }],
    },
    {
      stageId: `SetupComplete`,
      stage: (
        <SetupComplete
          closeModal={handleCloseModal}
          phoneNumber={stageConfig.selectedPhone.phoneNumber}
          activateVoice={stageConfig.activateVoice}
          linkIvr={stageConfig.linkIvr}
          ivr={stageConfig.ivr}
          setInitState={setInitState}
        />
      ),
      nextStages: [],
    },
  ];

  const stages = getStagesList(stagesSchema);

  const getFormTitle = (stage: number) => `Configure voice – step ${stage} of ${stages.length}`;

  const handleStepChange = (stepChange: { previousStep: number; activeStep: number }) => {
    saveWizardStep(stepChange.activeStep);
  };

  const getModalContent = (
    <StyledStepWizard onStepChange={handleStepChange} initialStep={1}>
      {stages.map(({ stageId, stage }, index) => (
        <StyledStepContainer
          key={index}
          data-aid={stageId}
          stageConfig={stageConfig}
          entityType={PhoneNumberCapability.VOICE}
        >
          {stage}
        </StyledStepContainer>
      ))}
    </StyledStepWizard>
  );

  return (
    <ModalContainer
      data-aid="configure-phone"
      title={getFormTitle(activeStep)}
      modalSize={ModalSize.LARGE}
      content={getModalContent}
      onCancel={handleCloseModal}
      disableOutsideClick={true}
    />
  );
};

const StyledStepWizard = styled(StepWizard)`
  overflow: hidden;
  text-align: left;
`;

export default ConfigureVoiceModal;
